import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, Warehouse, Printer } from "lucide-react";
import { api, errMsg, eur, dt, today, download } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { PageHeader, Field, Empty } from "@/components/Common";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrdineFornitore } from "@/components/OrdineFornitore";
import { OrdiniList } from "@/components/OrdiniList";
import { DateField } from "@/components/DateField";

const ALL = "__all";
const qn = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 3 });

const Giacenze = ({ g }) => (
  !g ? null : g.righe.length === 0 ? <Empty text="Nessun articolo gestito a magazzino: attiva l'opzione nella scheda articolo" /> : (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <Table data-testid="stock-table">
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Codice</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right">Carichi</TableHead><TableHead className="text-right">Scarichi</TableHead>
          <TableHead className="text-right">Giacenza</TableHead><TableHead className="text-right">Costo unit.</TableHead><TableHead className="text-right">Valore</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {g.righe.map((r) => (
            <TableRow key={r.product_id} data-testid={`stock-row-${r.product_id}`}>
              <TableCell className="font-mono text-xs">{r.codice}</TableCell><TableCell className="font-medium">{r.descrizione}</TableCell>
              <TableCell className="text-right font-mono">{qn(r.carichi)}</TableCell><TableCell className="text-right font-mono">{qn(r.scarichi)}</TableCell>
              <TableCell className={`text-right font-mono font-semibold ${r.giacenza < 0 || (r.scorta_minima > 0 && r.giacenza < r.scorta_minima) ? "text-rose-600" : ""}`} data-testid={`stock-qty-${r.product_id}`}>{qn(r.giacenza)} {r.unita}{r.scorta_minima > 0 && r.giacenza < r.scorta_minima && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-sans">sotto scorta</span>}</TableCell>
              <TableCell className="text-right font-mono">{eur(r.costo_unitario)}</TableCell>
              <TableCell className="text-right font-mono" data-testid={`stock-value-${r.product_id}`}>{eur(r.valore)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-slate-50 font-bold"><TableCell colSpan={6}>Valore totale rimanenze</TableCell><TableCell className="text-right font-mono" data-testid="stock-total-value">{eur(g.valore_totale)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  )
);

const Movimenti = ({ rows, onDelete, write }) => (
  rows.length === 0 ? <Empty text="Nessun movimento" /> : (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <Table data-testid="movements-table">
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Data</TableHead><TableHead>Articolo</TableHead><TableHead>Causale</TableHead><TableHead>Documento</TableHead>
          <TableHead className="text-right">Carico</TableHead><TableHead className="text-right">Scarico</TableHead><TableHead className="text-right">Giacenza</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((m, i) => (
            <TableRow key={m.id || i} data-testid={`movement-row-${i}`}>
              <TableCell className="font-mono text-xs">{dt(m.data)}</TableCell>
              <TableCell className="text-sm"><span className="font-mono text-xs text-slate-600">{m.codice}</span> {m.descrizione}</TableCell>
              <TableCell className="text-sm">{m.causale}</TableCell><TableCell className="text-sm">{m.documento}</TableCell>
              <TableCell className="text-right font-mono text-emerald-700">{m.segno > 0 ? qn(m.quantita) : ""}</TableCell>
              <TableCell className="text-right font-mono text-rose-600">{m.segno < 0 ? qn(m.quantita) : ""}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{qn(m.saldo)}</TableCell>
              <TableCell>{m.manuale && m.documento !== "Ordine fornitore" && write && <Button variant="ghost" size="icon" data-testid={`delete-movement-${i}`} onClick={() => onDelete(m.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
);

const NEW_MOV = { product_id: "", data: today(), tipo: "carico", quantita: 1, costo_unitario: 0, causale: "" };

const MovDialog = ({ mov, setMov, products, onSave }) => (
  <Dialog open={!!mov} onOpenChange={(o) => !o && setMov(null)}>
    <DialogContent data-testid="movement-dialog">
      <DialogHeader><DialogTitle>Nuovo movimento</DialogTitle></DialogHeader>
      {mov && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Articolo" className="sm:col-span-2">
            <Select value={mov.product_id || undefined} onValueChange={(v) => setMov({ ...mov, product_id: v })}>
              <SelectTrigger data-testid="movement-product-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.codice ? `${p.codice} · ` : ""}{p.descrizione}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Tipo">
            <Select value={mov.tipo} onValueChange={(v) => setMov({ ...mov, tipo: v })}>
              <SelectTrigger data-testid="movement-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="carico">Carico</SelectItem><SelectItem value="scarico">Scarico</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Data"><DateField testid="movement-date-input" value={mov.data} onChange={(v) => setMov({ ...mov, data: v })} /></Field>
          <Field label="Quantità"><Input data-testid="movement-qty-input" type="number" step="0.001" value={mov.quantita} onChange={(e) => setMov({ ...mov, quantita: e.target.value })} /></Field>
          {mov.tipo === "carico" && <Field label="Costo unitario €"><Input data-testid="movement-cost-input" type="number" step="0.01" value={mov.costo_unitario} onChange={(e) => setMov({ ...mov, costo_unitario: e.target.value })} /></Field>}
          <Field label="Causale (es. rimanenza iniziale, rettifica inventario)" className="sm:col-span-2"><Input data-testid="movement-causale-input" value={mov.causale} onChange={(e) => setMov({ ...mov, causale: e.target.value })} /></Field>
        </div>
      )}
      <DialogFooter><Button data-testid="save-movement-button" disabled={!mov?.product_id} onClick={onSave}>Salva</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export default function Magazzino() {
  const { user } = useAuth();
  const write = canWrite(user);
  const [st, setSt] = useState(null);
  const [al, setAl] = useState(today());
  const [g, setG] = useState(null);
  const [rows, setRows] = useState([]);
  const [pid, setPid] = useState(ALL);
  const [products, setProducts] = useState([]);
  const [mov, setMov] = useState(null);
  const [ordini, setOrdini] = useState([]);

  const load = useCallback(() => {
    api.get("/magazzino/ordini").then((r) => setOrdini(r.data)).catch(() => {});
    api.get("/magazzino/giacenze", { params: { al } }).then((r) => setG(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/magazzino/movimenti", { params: { product_id: pid === ALL ? "" : pid } }).then((r) => setRows([...r.data].reverse())).catch((e) => toast.error(errMsg(e)));
  }, [al, pid]);
  useEffect(() => {
    api.get("/magazzino/stato").then((r) => setSt(r.data));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.a_magazzino)));
  }, []);
  useEffect(() => { if (st?.attivo) load(); }, [st, load]);

  const setMetodo = async (metodo) => {
    try { await api.put("/magazzino/metodo", { metodo }); setSt({ ...st, metodo }); toast.success(`Valorizzazione: ${st.metodi[metodo]}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    try { await api.post("/magazzino/movimenti", { ...mov, quantita: Number(mov.quantita), costo_unitario: Number(mov.costo_unitario || 0) }); toast.success("Movimento registrato"); setMov(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/magazzino/movimenti/${id}`); toast.success("Movimento eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = (path, params) => download(path, params).catch((e) => toast.error(e.message));

  if (!st) return null;
  if (!st.attivo) return (
    <div data-testid="warehouse-inactive" className="mx-auto mt-16 max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center">
      <Warehouse className="mx-auto mb-3 h-10 w-10 text-slate-400" />
      <h1 className="font-display text-xl font-bold">Magazzino fiscale non attivo</h1>
      <p className="mt-2 text-sm text-slate-600">{st.abilitato ? `Il modulo è scaduto il ${dt(st.scadenza)}.` : "Il modulo è un pacchetto aggiuntivo."} Chiedi l'attivazione al tuo studio.</p>
    </div>
  );

  return (
    <div>
      <PageHeader title="Magazzino fiscale" subtitle={`Carichi e scarichi automatici da fatture e DDT${st.scadenza ? ` · modulo attivo fino al ${dt(st.scadenza)}` : ""}`}>
        {write && <OrdineFornitore onDone={load} />}
        {write && <Button data-testid="add-movement-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setMov({ ...NEW_MOV })}><Plus className="mr-2 h-4 w-4" />Movimento manuale</Button>}
      </PageHeader>
      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <Field label="Valorizzazione">
          <Select value={st.metodo} onValueChange={setMetodo} disabled={!write}>
            <SelectTrigger data-testid="valuation-method-select" className="w-60"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(st.metodi).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Valorizza al"><DateField clearable testid="valuation-date-input" value={al} className="w-44" onChange={(v) => setAl(v)} /></Field>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" data-testid="export-inventory-button" onClick={() => dl("/magazzino/inventario.csv", { al })}><Download className="mr-2 h-4 w-4" />Inventario CSV</Button>
          <Button variant="outline" data-testid="export-register-button" onClick={() => dl("/magazzino/registro.csv", { anno: al.slice(0, 4) })}><Download className="mr-2 h-4 w-4" />Registro CSV {al.slice(0, 4)}</Button>
          <Button variant="outline" data-testid="print-inventory-button" onClick={() => dl("/magazzino/inventario.pdf", { al })}><Printer className="mr-2 h-4 w-4" />Stampa inventario PDF</Button>
          <Button variant="outline" data-testid="print-register-button" onClick={() => dl("/magazzino/registro.pdf", { anno: al.slice(0, 4) })}><Printer className="mr-2 h-4 w-4" />Stampa registro PDF</Button>
        </div>
      </div>
      <Tabs defaultValue="giacenze">
        <TabsList><TabsTrigger value="giacenze" data-testid="tab-stock">Giacenze</TabsTrigger><TabsTrigger value="movimenti" data-testid="tab-movements">Movimenti</TabsTrigger><TabsTrigger value="ordini" data-testid="tab-orders">Ordini fornitori</TabsTrigger></TabsList>
        <TabsContent value="giacenze" className="mt-4"><Giacenze g={g} /></TabsContent>
        <TabsContent value="movimenti" className="mt-4 space-y-4">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="movement-filter-select" className="w-72 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tutti gli articoli</SelectItem>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.codice ? `${p.codice} · ` : ""}{p.descrizione}</SelectItem>)}</SelectContent>
          </Select>
          {pid !== ALL && <Button variant="outline" className="ml-2 bg-white" data-testid="print-product-card-button" onClick={() => dl("/magazzino/scheda.pdf", { product_id: pid })}><Printer className="mr-2 h-4 w-4" />Stampa scheda articolo PDF</Button>}
          <Movimenti rows={rows} onDelete={del} write={write} />
        </TabsContent>
        <TabsContent value="ordini" className="mt-4 space-y-4">
          {write && <Button variant="outline" className="bg-white" data-testid="generate-proposals-button" onClick={() => api.post("/magazzino/proposte").then((r) => { toast.success(r.data.create ? `${r.data.create} ${r.data.create === 1 ? "proposta d'ordine creata" : "proposte d'ordine create"}` : "Nessuna proposta: servono articoli sotto scorta con fornitore abituale e senza ordini aperti"); load(); }).catch((e) => toast.error(errMsg(e)))}>Genera proposte di riordino</Button>}
          <OrdiniList rows={ordini} write={write} onChange={load} />
        </TabsContent>
      </Tabs>
      <MovDialog mov={mov} setMov={setMov} products={products} onSave={save} />
    </div>
  );
}
